import { useState } from 'react';
import {
  Plus,
  Loader2,
  Building2,
  ArrowLeft,
  DollarSign,
  FileText,
  CreditCard,
  Receipt,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { StatCard } from '@/components/ui/StatCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import {
  useCompanies,
  useCompany,
  useCreateCompany,
  useCompanyDashboard,
  useCompanyInvoices,
  useCreateCompanyInvoice,
} from '@/hooks/api/useCompanies';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import type {
  Company,
  CompanyInvoice,
  CompanyDashboard,
  CreateCompanyPayload,
  CompanyStatus,
  CompanyInvoiceStatus,
  CreateInvoicePayload,
} from '@/services/api/companies.api';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<CompanyStatus, { bg: string; text: string; label: string }> = {
  active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' },
  inactive: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Inactive' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' },
};

const INVOICE_STATUS_STYLES: Record<CompanyInvoiceStatus, { bg: string; text: string; label: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Draft' },
  issued: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Issued' },
  paid: { bg: 'bg-green-100', text: 'text-green-700', label: 'Paid' },
  overdue: { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-400', label: 'Cancelled' },
};

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function CompaniesPage() {
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const propertyId = useSelectedPropertyId();

  const { data: companiesData, isLoading } = useCompanies({
    q: searchTerm || undefined,
    status: statusFilter || undefined,
    propertyId: propertyId || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const companies: Company[] = companiesData?.data ?? [];
  const totalPages = companiesData?.meta?.totalPages ?? 1;

  if (selectedCompanyId !== null) {
    return (
      <CompanyDetail
        companyId={selectedCompanyId}
        onBack={() => setSelectedCompanyId(null)}
      />
    );
  }

  return (
    <div>
      <PageHeader
        title="Companies"
        description="Manage corporate accounts and direct billing"
        actions={
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus size={16} aria-hidden="true" />
            Add Company
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input
          type="text"
          placeholder="Search companies..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-72"
          aria-label="Search companies"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label="Filter by status"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : companies.length === 0 ? (
        <EmptyState icon={Building2} title="No companies" description="Create your first corporate account." actionLabel="Add Company" onAction={() => setShowCreateForm(true)} />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Account Code</Th>
                  <Th>Type</Th>
                  <Th>Direct Bill</Th>
                  <Th>Status</Th>
                  <Th>Balance</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {companies.map((c) => {
                  const stat = STATUS_STYLES[c.status];
                  return (
                    <tr
                      key={c.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedCompanyId(c.id)}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{c.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-mono">{c.accountCode}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{c.companyType}</td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                          c.directBillPrivileges ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
                        )}>
                          {c.directBillPrivileges ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', stat.bg, stat.text)}>
                          {stat.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-medium">${c.balance}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <SlideOver isOpen={showCreateForm} onClose={() => setShowCreateForm(false)} title="Add Company">
        <CompanyForm onClose={() => setShowCreateForm(false)} />
      </SlideOver>
    </div>
  );
}

// ─── Company Detail ──────────────────────────────────────────────────────────

function CompanyDetail({ companyId, onBack }: { companyId: string; onBack: () => void }) {
  const { data: dashboardData } = useCompanyDashboard(companyId);
  const { data: invoicesData, isLoading: invoicesLoading } = useCompanyInvoices(companyId);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);

  // Fetch the specific company directly (works on deep-link / refresh too)
  const { data: company } = useCompany(companyId);

  const dashboard: CompanyDashboard | undefined = dashboardData;
  const invoices: CompanyInvoice[] = invoicesData?.data ?? [];

  return (
    <div>
      <div className="mb-6">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4"
        >
          <ArrowLeft size={16} />
          Back to Companies
        </button>
        <PageHeader
          title={company?.name ?? 'Company'}
          description={`Account: ${company?.accountCode ?? ''} | ${company?.companyType ?? ''}`}
        />
      </div>

      {/* Dashboard KPIs */}
      {dashboard && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={DollarSign}
            value={`$${dashboard.directBillLedger}`}
            label="Direct Bill Ledger"
            borderColor="border-l-blue-500"
          />
          <StatCard
            icon={CreditCard}
            value={`$${dashboard.accountsReceivable}`}
            label="Accounts Receivable"
            borderColor="border-l-orange-500"
          />
          <StatCard
            icon={FileText}
            value={`$${dashboard.totalInvoices}`}
            label="Total Invoices"
            borderColor="border-l-purple-500"
          />
          <StatCard
            icon={Receipt}
            value={`$${dashboard.totalPayments}`}
            label="Total Payments"
            borderColor="border-l-green-500"
          />
        </div>
      )}

      {/* Invoices */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Invoices</h3>
        <Button size="sm" onClick={() => setShowInvoiceForm(true)}>
          <Plus size={14} aria-hidden="true" />
          New Invoice
        </Button>
      </div>

      {invoicesLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : invoices.length === 0 ? (
        <EmptyState icon={FileText} title="No invoices" description="Create an invoice for this company." />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Invoice #</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                  <Th>Due Date</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoices.map((inv) => {
                  const stat = INVOICE_STATUS_STYLES[inv.status];
                  return (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 font-mono">INV-{inv.id.slice(0, 8).toUpperCase()}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">${inv.amount}</td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', stat.bg, stat.text)}>
                          {stat.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(inv.dueDate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver isOpen={showInvoiceForm} onClose={() => setShowInvoiceForm(false)} title="New Invoice">
        <InvoiceForm companyId={companyId} onClose={() => setShowInvoiceForm(false)} />
      </SlideOver>
    </div>
  );
}

// ─── Company Form ────────────────────────────────────────────────────────────

function CompanyForm({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [accountCode, setAccountCode] = useState('');
  const [type, setType] = useState('LOCAL');
  const [directBill, setDirectBill] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState('net_30');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const createMutation = useCreateCompany();

  function clearError() {
    if (createMutation.isError) createMutation.reset();
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: CreateCompanyPayload = {
      name,
      accountCode,
      companyType: type || undefined,
      directBillPrivileges: directBill,
      paymentTerms: paymentTerms || undefined,
      email: email || undefined,
      phone: phone || undefined,
    };
    createMutation.mutate(payload, { onSuccess: () => onClose() });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="co-name" className="block text-sm font-medium text-gray-700 mb-1">Company Name <span className="text-red-500">*</span></label>
        <input id="co-name" type="text" required value={name} onChange={(e) => { setName(e.target.value); clearError(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="co-code" className="block text-sm font-medium text-gray-700 mb-1">Account Code <span className="text-red-500">*</span></label>
        <input id="co-code" type="text" required value={accountCode} onChange={(e) => { setAccountCode(e.target.value); clearError(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="co-type" className="block text-sm font-medium text-gray-700 mb-1">Type</label>
        <select id="co-type" value={type} onChange={(e) => setType(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="LOCAL">Local</option>
          <option value="CORPORATE">Corporate</option>
          <option value="TRAVEL_AGENCY">Travel Agency</option>
        </select>
      </div>
      <div className="flex items-center gap-2">
        <input id="co-db" type="checkbox" checked={directBill} onChange={(e) => setDirectBill(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        <label htmlFor="co-db" className="text-sm font-medium text-gray-700">Enable Direct Bill</label>
      </div>
      <div>
        <label htmlFor="co-terms" className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
        <select id="co-terms" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="net_30">Net 30</option>
          <option value="net_60">Net 60</option>
          <option value="net_90">Net 90</option>
        </select>
      </div>
      <div>
        <label htmlFor="co-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
        <input id="co-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="co-phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
        <input id="co-phone" type="text" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      {createMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(createMutation.error)}
        </p>
      )}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending}>Create Company</Button>
      </div>
    </form>
  );
}

// ─── Invoice Form ────────────────────────────────────────────────────────────

function InvoiceForm({ companyId, onClose }: { companyId: string; onClose: () => void }) {
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');

  const createMutation = useCreateCompanyInvoice();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: CreateInvoicePayload = {
      amount: amount.trim(),
      dueDate,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
    createMutation.mutate({ companyId, payload }, { onSuccess: () => onClose() });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="inv-amt" className="block text-sm font-medium text-gray-700 mb-1">Amount <span className="text-red-500">*</span></label>
        <input id="inv-amt" type="number" required min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="inv-due" className="block text-sm font-medium text-gray-700 mb-1">Due Date <span className="text-red-500">*</span></label>
        <input id="inv-due" type="date" required value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="inv-notes" className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <input id="inv-notes" type="text" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending}>Create Invoice</Button>
      </div>
    </form>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
